import { useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';

import { useSignalChunkPlotData } from './useSignalChunkPlotData';

interface PlotlyWindow {
  Plotly?: {
    relayout: (el: HTMLElement, update: Record<string, unknown>) => void;
    restyle: (el: HTMLElement, update: Record<string, unknown>, traces: number[]) => void;
  };
}

const config: Partial<Plotly.Config> = {
  displayModeBar: false,
};

export interface SignalChunkPlotProps {
  recordingId: number;
  chunkIdx: number;
}

export const SignalChunkPlot: React.FC<SignalChunkPlotProps> = ({ recordingId, chunkIdx, ...props }) => {
  const plotParams = useSignalChunkPlotData(recordingId, chunkIdx);
  const graphDivRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const plotly = (window as unknown as PlotlyWindow).Plotly;
    const mql = window.matchMedia('print');

    const handleChange = (e: MediaQueryListEvent) => {
      if (!graphDivRef.current) { return; }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const gd = graphDivRef.current as any;

      if (e.matches) {
        // Capture current axis range before any changes so user's zoom is preserved
        const xrange: [unknown, unknown] | undefined = gd._fullLayout?.xaxis?.range
          ? [...gd._fullLayout.xaxis.range] as [unknown, unknown]
          : undefined;

        // Switch WebGL traces to SVG — WebGL canvas is not captured in print snapshots
        plotly?.restyle(graphDivRef.current, { type: 'scatter' }, [0, 2]);

        const width = graphDivRef.current.parentElement?.offsetWidth ?? graphDivRef.current.offsetWidth;
        const update: Record<string, unknown> = { autosize: false };
        if (width > 0) { update.width = width; }
        if (xrange) {
          update['xaxis.range[0]'] = xrange[0];
          update['xaxis.range[1]'] = xrange[1];
          update['xaxis.autorange'] = false;
        }
        plotly?.relayout(graphDivRef.current, update);
      } else {
        const xrange: [unknown, unknown] | undefined = gd._fullLayout?.xaxis?.range
          ? [...gd._fullLayout.xaxis.range] as [unknown, unknown]
          : undefined;

        // Restore WebGL for screen performance
        plotly?.restyle(graphDivRef.current, { type: 'scattergl' }, [0, 2]);

        const update: Record<string, unknown> = { width: undefined, autosize: true };
        if (xrange) {
          update['xaxis.range[0]'] = xrange[0];
          update['xaxis.range[1]'] = xrange[1];
          update['xaxis.autorange'] = false;
        }
        plotly?.relayout(graphDivRef.current, update);
      }
    };

    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return (
    <Plot
      {...plotParams}
      config={config}
      className={'signal-chart'}
      style={{ width: '100%' }}
      useResizeHandler
      onInitialized={(_, graphDiv) => {
        graphDivRef.current = graphDiv as HTMLElement;
      }}
    />
  );
};
